import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCheck, faCheckDouble, faSpinner } from '@fortawesome/free-solid-svg-icons';
import './AnnouncementCard.css';
import { MyAnnouncement } from '../../services/announcementService';

interface AnnouncementCardProps {
  ann: MyAnnouncement;
  pConfig: { color: string; label: string };
  expanded: boolean;
  onToggleExpand: () => void;
  markingReadId: number | null;
  onMarkRead: (id: number) => void;
  onOpen?: () => void;
  layout?: 'grid' | 'row';
}

const AnnouncementCard = React.forwardRef<HTMLDivElement, AnnouncementCardProps>(
  ({ ann, pConfig, expanded, onToggleExpand, markingReadId, onMarkRead, onOpen, layout = 'grid' }, ref) => {
    const { t } = useTranslation();
    const messageRef = useRef<HTMLParagraphElement>(null);
    const [clamped, setClamped] = useState(false);

    useEffect(() => {
      if (layout === 'row') {
        setClamped(false);
        return;
      }
      const el = messageRef.current;
      if (el) setClamped(el.scrollHeight > el.clientHeight + 1);
    }, [ann.message, layout]);

    const readMoreControl = clamped ? (
      <button
        type="button"
        className={`announcement-card-readmore${onOpen ? ' announcement-card-readmore--nav' : ''}`}
        onClick={onOpen ? onOpen : onToggleExpand}
      >
        {onOpen ? (
          <>
            {t('home:announcements.readMore')} <FontAwesomeIcon icon={faArrowRight} />
          </>
        ) : expanded ? (
          t('home:announcements.readLess')
        ) : (
          t('home:announcements.readMore')
        )}
      </button>
    ) : null;

    const markAsReadControl = ann.is_read ? (
      <span className="announcement-read-badge">
        <FontAwesomeIcon icon={faCheckDouble} /> {t('home:announcements.read')}
      </span>
    ) : (
      <button
        type="button"
        className="announcement-mark-read-btn"
        onClick={() => onMarkRead(ann.id)}
        disabled={markingReadId === ann.id}
      >
        {markingReadId === ann.id ? (
          <>
            <FontAwesomeIcon icon={faSpinner} spin /> {t('home:announcements.marking')}
          </>
        ) : (
          <>
            <FontAwesomeIcon icon={faCheck} /> {t('home:announcements.markAsRead')}
          </>
        )}
      </button>
    );

    return (
      <div
        ref={ref}
        className={`announcement-card${layout === 'row' ? ' announcement-card--row' : ''}`}
        style={{ borderLeftColor: pConfig.color }}
      >
        {layout === 'row' ? (
          <div className="announcement-card-main">
            <div className="announcement-card-header announcement-card-header--row">
              <div className="announcement-card-header-left">
                <span
                  className="announcement-priority-badge"
                  style={{ background: `${pConfig.color}14`, color: pConfig.color }}
                >
                  {pConfig.label}
                </span>
                <span className="announcement-date">
                  {new Date(ann.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                </span>
              </div>
              <div className="announcement-card-header-actions">{markAsReadControl}</div>
            </div>
            <h4 className="announcement-card-title">{ann.title}</h4>
            <p className="announcement-card-message announcement-card-message--full">{ann.message}</p>
          </div>
        ) : (
          <>
            <div className="announcement-card-header">
              <span
                className="announcement-priority-badge"
                style={{ background: `${pConfig.color}14`, color: pConfig.color }}
              >
                {pConfig.label}
              </span>
              <span className="announcement-date">
                {new Date(ann.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
              </span>
            </div>
            <div className="announcement-card-main">
              <h4 className="announcement-card-title">{ann.title}</h4>
              <p ref={messageRef} className={`announcement-card-message${expanded ? ' expanded' : ''}`}>
                {ann.message}
              </p>
            </div>
            <div className="announcement-card-actions">
              <div className="announcement-card-actions-left">{readMoreControl}</div>
              <div className="announcement-card-actions-right">{markAsReadControl}</div>
            </div>
          </>
        )}
      </div>
    );
  },
);

AnnouncementCard.displayName = 'AnnouncementCard';

export default AnnouncementCard;